import React, { useState, useEffect } from 'react';
import { 
  AppBar, Toolbar, Typography, Box, IconButton, 
  Drawer, List, ListItem, ListItemIcon, ListItemText, Divider 
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LogoutIcon from '@mui/icons-material/Logout';
import CategoryIcon from '@mui/icons-material/Category';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import PeopleIcon from '@mui/icons-material/People'; 
// --- 그룹 관리용 아이콘 추가 ---
import GroupIcon from '@mui/icons-material/Group'; 
import { useNavigate, Link, useLocation } from 'react-router-dom';

import logo from '../assets/logo.png'; 

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({ id: null, role: null, name: null });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const LOGO_CORLOR = '#E2D5C3'; 
  const THEME_NAVY = '#1a233a';

  useEffect(() => {
    const id = localStorage.getItem('id');
    const name = localStorage.getItem('name');
    const role = localStorage.getItem('role');
    setUser({ id: id, name: name, role: role });
  }, [location]);

  const handleLogout = () => {
    localStorage.clear();
    setUser({ id: null, name: null, role: null });
    setDrawerOpen(false);
    alert('로그아웃 되었습니다.');
    navigate('/login');
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuList = () => (
    <Box
      sx={{ width: 280 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      {/* 유저 프로필 섹션 */}
      <Box sx={{ p: 3, backgroundColor: LOGO_CORLOR, color: THEME_NAVY }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <img src={logo} alt="늘수어 로고" style={{ height: '35px', borderRadius: '50%' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>늘수어</Typography>
        </Box>
        {user.id ? (
          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {user.name}님 ({user.role})
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>로그인이 필요합니다</Typography>
        )}
      </Box>
      
      <List sx={{ px: 1 }}>
        {/* 대시보드 - 관리자 및 교사 전용 */}
        {user.id && (
          <ListItem button component={Link} to="/dashboard">
            <ListItemIcon><DashboardIcon color="primary" /></ListItemIcon>
            <ListItemText primary="대시보드" />
          </ListItem>
        )}

        {/* 학습하기 - 공통 */}
        {user.id && (
          <ListItem button component={Link} to="/category-select">
            <ListItemIcon><AssignmentIcon sx={{ color: '#2e7d32' }} /></ListItemIcon>
            <ListItemText primary="수어 학습하기" />
          </ListItem>
        )}

        <Divider sx={{ my: 1 }} />
        
        {/* 콘텐츠 관리 - 관리자 및 교사 전용 */}
        {user.id && (user.role === 'ADMIN' || user.role === 'TEACHER') && (
          <>
            <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', fontWeight: 'bold' }}>
              콘텐츠 관리
            </Typography>
            <ListItem button component={Link} to="/list">
              <ListItemIcon><HomeIcon /></ListItemIcon>
              <ListItemText primary="문제 목록 관리" />
            </ListItem>
            <ListItem button component={Link} to="/category">
              <ListItemIcon><CategoryIcon /></ListItemIcon>
              <ListItemText primary="카테고리 관리" />
            </ListItem>
            <ListItem button component={Link} to="/video">
              <ListItemIcon><VideoLibraryIcon sx={{ color: THEME_NAVY }} /></ListItemIcon>
              <ListItemText primary="비디오 마스터 관리" />
            </ListItem>
          </>
        )}

        {/* 시스템 설정 - 관리자 전용 */}
        {user.id && user.role === 'ADMIN' && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', fontWeight: 'bold' }}>
              시스템 설정
            </Typography>
            {/* 사용자 관리 */}
            <ListItem button component={Link} to="/admin/users">
              <ListItemIcon><PeopleIcon sx={{ color: '#1976d2' }} /></ListItemIcon>
              <ListItemText primary="사용자 관리" />
            </ListItem>
            {/* --- 사용자 그룹 관리 메뉴 추가 --- */}
            <ListItem button component={Link} to="/admin/groups">
              <ListItemIcon><GroupIcon sx={{ color: '#6a1b9a' }} /></ListItemIcon>
              <ListItemText primary="그룹 관리" />
            </ListItem>
            <ListItem button component={Link} to="/common-code">
              <ListItemIcon><SettingsIcon sx={{ color: '#546e7a' }} /></ListItemIcon>
              <ListItemText primary="공통코드 관리" />
            </ListItem>
          </>
        )}
      </List>
      
      <Divider />
      
      <List sx={{ px: 1 }}>
        {user.id ? (
          <ListItem button onClick={handleLogout}>
            <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
            <ListItemText primary="로그아웃" sx={{ color: 'error.main' }} />
          </ListItem>
        ) : (
          <ListItem button component={Link} to="/login">
            <ListItemIcon><LogoutIcon /></ListItemIcon>
            <ListItemText primary="로그인" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" sx={{ backgroundColor: LOGO_CORLOR, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            sx={{ mr: 1, color: THEME_NAVY }}
            onClick={toggleDrawer(true)}
          >
            <MenuIcon />
          </IconButton>

          <Box 
            component={Link} 
            to={user.role === 'ADMIN' || user.role === 'TEACHER' || user.role === 'STUDENT' ? "/category-select" : "/login"} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none', 
              color: 'inherit',
              flexGrow: 1,
              gap: 1.5 
            }}
          >
            <img 
              src={logo} 
              alt="늘수어 로고" 
              style={{ height: '42px', width: 'auto', borderRadius: '50%' }} 
            />
            <Typography
              variant="h6"
              sx={{ 
                fontWeight: 'bold', 
                fontSize: '1.4rem',
                color: THEME_NAVY,
                display: { xs: 'none', sm: 'block' } 
              }}
            >
              늘수어
            </Typography>
          </Box>

          {user.id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#555555', fontWeight: 500 }}>
                {user.name}님
              </Typography>
              <Box 
                sx={{ 
                  px: 1, py: 0.2, borderRadius: 1, 
                  backgroundColor: THEME_NAVY, color: 'white', fontSize: '0.7rem' 
                }}
              >
                {user.role}
              </Box>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        {menuList()}
      </Drawer>
    </>
  );
};

export default Navbar;